// React
import { useRef } from "react"

// I18n
import { Trans, useTrans } from "@/i18n"

// Forms
import { Yup } from "@/lib/forms"
import { FormikProvider, useFormik } from "formik"
import { SelectButton, Select, FormGroup } from "@/components/form-controls"
import { FiChevronDown } from "react-icons/fi"
import {
	FormikError,
	FormikErrors,
	FormikInput,
	FormikLabel,
	FormikSubmitButton,
} from "@/components/form-controls/formik"
import { LabelSubtitle } from "@/components/form-controls/Label"

// DateTime
import { DateTime } from "@/lib/dates"
import { dateFormat, apiDateFormat } from "@/constants/constants"

// UI
import { StepFailedOverlay } from "@/pages/investor/pages/identity/_components/StepFailedOverlay"
import { StepCompletedOverlay } from "@/pages/investor/pages/identity/_components/StepCompletedOverlay"
import { Heading } from "@/components/Typography"
import { CardBody, CardFooter, CardWrapper } from "@/components/Card"
import { Disclaimer } from "@/components/Disclaimer"

// Context
import { useToasts } from "@/context/toasts"
import { useCurrentUserMangoPayStatus } from "@/context/user"

// GraphQL
import {
	KycVerificationDocumentType,
	RiskKycStateChoices,
	useUserKycStatusQuery,
} from "@/api/graphql"
import { usersKycDocumentsUploadCreate } from "@/api/rest/generated/users/users"
import {
	KycVerificationDocument,
	DocumentTypeEnum,
} from "@/api/rest/generated/@types"

// Tracking
import { captureMessage } from "@/lib/sentry"

/**
 * InvestorIdentityForm
 * @returns
 */
export const InvestorIdentityForm = () => {
	// Context
	const toasts = useToasts()
	const mangopayStatus = useCurrentUserMangoPayStatus()

	// Translations
	const t = useTrans("investor")

	// Query
	const { data, refetch } = useUserKycStatusQuery()

	// Form
	const formRef = useRef<HTMLFormElement>(null!)
	const form = useFormik({
		initialValues: {
			document: "",
			document_back: "",
			document_type: undefined as DocumentTypeEnum | undefined,
			expiry_date: "",
			full_first_name: "",
		},
		validationSchema: Yup.object().shape({
			expiry_date: Yup.string()
				.required(
					t(
						"investor:investor.identity.form.field.expiry-date.error.required",
					),
				)
				.matches(
					/^([0-9]{2})-([0-9]{2})-([0-9]{4})$/, // DD-MM-YYYY
					"investor:investor.identity.form.field.expiry-date.error.format",
				),
			full_first_name: Yup.string().required(
				t(
					"investor:investor.identity.form.field.first-name.error.required",
				),
			),
		}),
		onSubmit: async (values, helpers) => {
			helpers.setStatus("")
			try {
				const uuid = data?.me?.uuid
				const response = await usersKycDocumentsUploadCreate(uuid, {
					document: values.document,
					document_back: values.document_back,
					document_type: values.document_type as DocumentTypeEnum,
					expiry_date: DateTime.fromFormat(
						values.expiry_date,
						dateFormat,
					).toFormat(apiDateFormat),
					full_first_name: values.full_first_name,
				} as KycVerificationDocument)

				// When payment has been confirmed
				if (response.ok === true) {
					toasts.addToast({
						variant: "success",
						id: `usersKycDocumentsUploadCreate-${Date.now()}`,
						text: t("investor:investor.identity.form.success"),
					})

					// Refetch data
					void refetch()

					// Scroll to top
					window.scrollTo(0, 0)
				} else {
					// Parse all fields in response, assuming these are errors!
					Object.keys(form.values).forEach((field) => {
						// @ts-expect-error - Because Fields can have any name
						if (response?.[field]) {
							// @ts-expect-error - Because Fields can have any name
							form.setFieldError(field, response?.[field]?.[0])
						}
					})

					// Log errors to sentry
					captureMessage("KYC submission failed", {
						level: "error",
						extra: {
							response,
						},
					})

					// Show generic error toast
					toasts.addToast({
						variant: "alert",
						id: `usersKycDocumentsUploadCreate-${Date.now()}`,
						text: t("investor:investor.identity.form.error"),
					})
				}
			} catch (e) {
				// Log errors to sentry
				captureMessage("KYC submission failed", {
					level: "error",
					extra: {
						error: e,
					},
				})

				// Show generic error toast
				toasts.addToast({
					variant: "alert",
					id: `usersKycDocumentsUploadCreate-${Date.now()}`,
					text: t("investor:investor.identity.form.error"),
				})
			}
		},
	})

	// When changing document_type
	async function onChangeDocumentType(type: DocumentTypeEnum) {
		form.resetForm()
		formRef.current.reset()
		void form.setFieldValue("document_type", type)
	}

	// Render
	return (
		<FormikProvider value={form}>
			<KycDisclaimer />

			<CardWrapper className="relative mt-4">
				{/** Show blocking overlay when mangopay status is false */}
				{mangopayStatus === false &&
				data?.me?.kyc?.state ===
					RiskKycStateChoices.VerificationRequested ? (
					<StepFailedOverlay
						title={t("investor:investor.identity.blocked.title")}
						message={t("investor:investor.identity.blocked.copy")}
					/>
				) : null}

				{/** Show blocking overlay when status is NOT requested */}
				{data?.me?.kyc?.state !== undefined &&
				data?.me?.kyc?.state !==
					RiskKycStateChoices.VerificationRequested ? (
					<StepCompletedOverlay
						state={data?.me?.kyc?.state}
						message={data?.me?.kyc?.human_readable_kyc_error}
						last_verified_on={data?.me?.kyc?.last_verified_on}
						documents={
							data?.me?.kyc?.documents?.filter(
								Boolean,
							) as KycVerificationDocumentType[]
						}
					/>
				) : null}

				{/* Form */}
				<form onSubmit={form.handleSubmit} ref={formRef}>
					<CardBody>
						<FormGroup
							heading={
								<>
									<Heading as="h3" styleAs="h6">
										{t(
											"investor:investor.identity.photo-requirements.instructions",
										)}
									</Heading>
									<ul className="ml-4 list-disc">
										<li>
											{t(
												"investor:investor.identity.photo-requirements.req.quality",
											)}
										</li>
										<li>
											{t(
												"investor:investor.identity.photo-requirements.req.lighting",
											)}
										</li>
										<li>
											{t(
												"investor:investor.identity.photo-requirements.req.visibility",
											)}
										</li>
										<li>
											{t(
												"investor:investor.identity.photo-requirements.req.edges",
											)}
										</li>
										<li>
											{t(
												"investor:investor.identity.photo-requirements.req.no-watermark",
											)}
										</li>
										<li>
											{t(
												"investor:investor.identity.photo-requirements.req.back-side",
											)}
										</li>
										<li>
											{t(
												"investor:investor.identity.photo-requirements.req.color",
											)}
										</li>
									</ul>
								</>
							}
						>
							<div className="space-y-6">
								<div className="relative flex space-x-6">
									{/** Example img */}
									<img
										className="align-center w-1/2"
										src={
											{
												default:
													"/assets/kyc/choose_id.jpg",
												driving_license:
													"/assets/kyc/driving_license_example.jpg",
												national_id_number:
													"/assets/kyc/national_id_example.jpg",
												passport:
													"/assets/kyc/passport_example.jpg",
												residence_permit:
													"/assets/kyc/residence_permit_example.jpg",
											}[
												form.values.document_type ||
													"default"
											]
										}
										alt={form.values.document_type}
									/>

									{/** Document Type */}
									<div className="relative flex-col">
										<FormikLabel>
											{t(
												"investor:investor.identity.form.field.document-type.label",
											)}
											<LabelSubtitle>
												{t(
													"investor:investor.identity.form.field.document-type.sublabel",
												)}
											</LabelSubtitle>
										</FormikLabel>
										<SelectButton className="w-full">
											{form.values.document_type ===
											undefined
												? t(
														"investor:investor.identity.form.field.document-type.label",
													)
												: t(
														`investor:investor.identity.form.field.document-type.options.${form.values.document_type}`,
													)}
											<FiChevronDown className={"ml-2"} />
											<Select
												name="document_type"
												className="h-100 absolute left-0 top-0 w-full cursor-pointer opacity-0"
												onChange={(event) =>
													void onChangeDocumentType(
														event.currentTarget
															.value as DocumentTypeEnum,
													)
												}
												value={
													form.values.document_type ??
													undefined
												}
											>
												{/** Default option */}
												<option
													disabled
													selected={
														form.values
															.document_type ===
														undefined
													}
												>
													{t(
														"investor:investor.identity.form.field.document-type.label",
													)}
												</option>
												{Object.entries(
													DocumentTypeEnum,
												).map(([_key, value]) => (
													<option
														value={value}
														key={value}
													>
														{t(
															`investor:investor.identity.form.field.document-type.options.${value}`,
														)}
													</option>
												))}
											</Select>
										</SelectButton>
									</div>
								</div>

								{form.values.document_type !== undefined && (
									<>
										{/** Document File Front */}
										<FormikLabel htmlFor="document">
											{t(
												"investor:investor.identity.form.field.photo-front-side.label",
											)}
											<LabelSubtitle>
												{t(
													"investor:investor.identity.form.field.photo.sublabel",
												)}
											</LabelSubtitle>
										</FormikLabel>
										<input
											type="file"
											name="document"
											onChange={(e) => {
												void form.setFieldValue(
													"document",
													e.target.files?.[0] as File,
												)
											}}
										/>
										<FormikError
											field="document"
											namespace="investor"
										/>

										{/** Document File Back */}
										{form.values.document_type !==
											DocumentTypeEnum.passport && (
											<>
												<FormikLabel htmlFor="document_back">
													{t(
														"investor:investor.identity.form.field.photo-back-side.label",
													)}
													<LabelSubtitle>
														{t(
															"investor:investor.identity.form.field.photo.sublabel",
														)}
													</LabelSubtitle>
												</FormikLabel>
												<input
													type="file"
													name="document_back"
													onChange={(event) => {
														void form.setFieldValue(
															"document_back",
															event.target
																.files?.[0] as File,
														)
													}}
												/>
												<FormikError
													field="document_back"
													namespace="investor"
												/>
											</>
										)}

										{/** First name */}
										<div>
											<FormikLabel htmlFor="full_first_name">
												{t(
													"investor:investor.identity.form.field.first-name.label",
												)}
												<LabelSubtitle>
													{t(
														"investor:investor.identity.form.field.first-name.sublabel",
													)}
												</LabelSubtitle>
											</FormikLabel>
											<FormikInput
												name="full_first_name"
												className="block w-full"
												classNameWrapper="lg:w-1/2"
												aria-label={t(
													"investor:investor.identity.form.field.first-name.label",
												)}
											/>
											<FormikError
												field="full_first_name"
												namespace="investor"
											/>
										</div>

										{/** Expiry date */}
										<div>
											<FormikLabel htmlFor="expiry_date">
												{t(
													"investor:investor.identity.form.field.expiry-date.label",
												)}
												<LabelSubtitle>
													{t(
														"investor:investor.identity.form.field.expiry-date.sublabel",
													)}
												</LabelSubtitle>
											</FormikLabel>
											<FormikInput
												name="expiry_date"
												placeholder={t(
													"investor:investor.identity.form.field.expiry-date.placeholder",
												)}
												className="block w-full"
												classNameWrapper="lg:w-1/2"
												aria-label={t(
													"investor:investor.identity.form.field.expiry-date.label",
												)}
											/>
											<FormikError
												field="expiry_date"
												namespace="investor"
											/>
										</div>
									</>
								)}

								<div>
									<FormikErrors i18nNamespace="investor" />
								</div>
							</div>
						</FormGroup>
					</CardBody>
					<CardFooter className="text-right">
						<FormikSubmitButton
							disabled={
								data?.me?.kyc?.state ===
								RiskKycStateChoices.VerificationSuccess
							}
						>
							{t("investor:investor.identity.form.button.submit")}
						</FormikSubmitButton>
					</CardFooter>
				</form>
			</CardWrapper>
		</FormikProvider>
	)
}

/**
 * KycDisclaimer
 * @returns
 */
const KycDisclaimer = () => {
	// i18n
	const t = useTrans("investor")

	return (
		<Disclaimer
			className="mt-3"
			title={t("investor:investor.identity.copy.verification.title")}
			message={
				<>
					<Trans
						i18nKey="investor:investor.identity.copy"
						components={{
							/* eslint-disable jsx-a11y/anchor-has-content */
							a: (
								<a
									className="font-medium text-secondary-300 hover:text-secondary-700"
									href="https://www.afm.nl/nl-nl/sector/registers/vergunningenregisters/beleggingsondernemingen/details?id=70DF7EAB-C7AA-E411-A3EB-005056BE011D"
									target="_blank"
									rel="noreferrer"
								/>
								/* eslint-enable jsx-a11y/anchor-has-content */
							),
						}}
					/>
					<br />
					<br />
					<strong>
						{t(
							"investor:investor.identity.copy.verification.email",
						)}
					</strong>
					<p>
						{t(
							"investor:investor.identity.copy.verification.failed",
						)}
					</p>
					<p>
						{t(
							"investor:investor.identity.copy.verification.support",
						)}{" "}
						<a
							target="_blank"
							rel="noreferrer"
							className="font-medium text-secondary-300 hover:text-secondary-700"
							href="mailto:info@zonhub.com"
						>
							info@zonhub.com
						</a>
					</p>
					<p>
						{t(
							"investor:investor.identity.copy.verification.faq.text",
						)}{" "}
						<a
							target="_blank"
							rel="noreferrer"
							className="font-medium text-secondary-300 hover:text-secondary-700"
							href="https://informatie.zonhub.com/a/solutions/articles/17000117135"
						>
							{t(
								"investor:investor.identity.copy.verification.faq",
							)}
						</a>
						.
					</p>
				</>
			}
		/>
	)
}
